import { getMapsUrl } from "./maps";
import { normalizeMeetingMode, normalizeOnlineProvider, normalizeVisibility, sanitizeOnlineUrl } from "./onlineMeeting";
// Pure canonical->legacy READ adapter for canonical-shaped subcollection docs.
import { canonicalToLegacyTemplate } from "./canonicalTemplateShape";
// Phase 1B: client recurrence/timezone converge onto the Canonical Service Engine
// (generated ESM mirror of functions/src/serviceEngine/). This adopts the hardened
// timezone conversion (approved defect correction) and the single authoritative
// recurrence matcher; the client retains only its display-facing weekday helper.
import {
  getDatePartsInTimeZone as engineGetDatePartsInTimeZone,
  buildDateFromPartsInTimeZone as engineBuildDateFromPartsInTimeZone,
} from "../serviceEngine/generated/time";
import {
  serviceTemplateMatchesParts as engineServiceTemplateMatchesParts,
  isOccurrenceCancelled as engineIsOccurrenceCancelled,
  applyOccurrenceOverride as engineApplyOccurrenceOverride,
} from "../serviceEngine/generated/recurrence";

export const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const DAY_LABELS = {
  sun: "Sunday",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
};

const DEFAULT_TIME_ZONE = "America/Los_Angeles";
const DEFAULT_DURATION = 120;
const DEFAULT_OPEN_BEFORE = 60;
const DEFAULT_CLOSE_AFTER = 0;
const DEFAULT_CLOSE_AFTER_END = 120;

function safeText(value, fallback = "") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  if (/^(undefined|null|nan)$/i.test(text)) return fallback;
  if (/invalid date/i.test(text)) return fallback;
  return text;
}

function clampInt(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, Math.round(number)));
}

function pad(value) {
  return String(value).padStart(2, "0");
}

function toDateInput(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateInput(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseLocalDateParts(value) {
  if (!value) return null;
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

export function getDatePartsInTimeZone(date, timeZone = DEFAULT_TIME_ZONE) {
  // Numeric parts come from the Canonical Service Engine (hardened: midnight
  // hour==24 -> 0). Weekday is a client-only display helper, computed exactly as
  // before via Intl on the same date+timezone (thin wrapper).
  const parts = engineGetDatePartsInTimeZone(date, timeZone);
  const weekday = normalizeDay(
    new Intl.DateTimeFormat("en-US", {
      timeZone: timeZone || DEFAULT_TIME_ZONE,
      weekday: "short",
    }).format(date)
  );
  return { ...parts, weekday };
}

export function buildDateFromPartsInTimeZone(year, month, day, hour, minute, timeZone = DEFAULT_TIME_ZONE) {
  return engineBuildDateFromPartsInTimeZone(year, month, day, hour, minute, timeZone);
}

function compareLocalParts(a, b) {
  if (!a || !b) return 0;
  return Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day);
}

function localPartsFromOrdinal(ordinalMs, timeZone = DEFAULT_TIME_ZONE) {
  const utcDate = new Date(ordinalMs);
  const parts = {
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  };
  const localNoon = buildDateFromPartsInTimeZone(parts.year, parts.month, parts.day, 12, 0, timeZone);
  return {
    ...parts,
    weekday: getDatePartsInTimeZone(localNoon, timeZone).weekday,
  };
}

function parseTime(value, fallbackHour = 9, fallbackMinute = 0) {
  const text = String(value || "").trim();
  const match = text.match(/^(\d{1,2})(?::(\d{1,2}))?/);
  if (match) {
    return {
      hour: clampInt(match[1], 0, 23, fallbackHour),
      minute: clampInt(match[2] || 0, 0, 59, fallbackMinute),
    };
  }
  return { hour: fallbackHour, minute: fallbackMinute };
}

function normalizeDay(value) {
  if (typeof value === "number") return DAY_KEYS[value] || "";
  const safe = String(value || "").trim().toLowerCase();
  if (!safe) return "";
  if (/^\d$/.test(safe)) return DAY_KEYS[Number(safe)] || "";
  return DAY_KEYS.find((key) => key === safe || DAY_LABELS[key].toLowerCase().startsWith(safe.slice(0, 3))) || "";
}

function inferDayFromTitle(value) {
  const text = String(value || "").toLowerCase();
  return DAY_KEYS.find((key) => text.includes(DAY_LABELS[key].toLowerCase())) || "";
}

function normalizeTemplateKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeFrequency(value, fallback = "weekly") {
  const text = safeText(value, fallback).toLowerCase().replace("_", "-");
  if (text === "one-time") return "one_time";
  return text;
}

function isGeneratedServiceInstance(template = {}) {
  const typeText = `${template.recordType || ""} ${template.kind || ""} ${template.sourceType || ""}`.toLowerCase();
  return (
    typeText.includes("occurrence") ||
    template.isGeneratedOccurrence === true ||
    template.generatedFromTemplate === true ||
    Boolean(template.occurrenceId || template.occurrenceDate || template.templateOccurrenceId || template.serviceDate || template.startsAt)
  );
}

function serviceTemplateIdentity(template = {}) {
  const schedule = template.schedule || {};
  const recurrence = template.recurrence || {};
  const explicit = normalizeTemplateKey(template.serviceTemplateId || template.templateId || template.templateKey || template.key);
  if (explicit) return explicit;
  const title = normalizeTemplateKey(template.title || template.name || template.serviceType || template.type || "service");
  const frequency = normalizeFrequency(
    typeof template.recurrence === "string" ? template.recurrence : recurrence.frequency || schedule.frequency || template.frequency,
    "weekly"
  );
  const dayPart = frequency === "monthly"
    ? `day-${recurrence.dayOfMonth || schedule.dayOfMonth || template.dayOfMonth || 1}`
    : frequency === "one_time"
      ? normalizeTemplateKey(recurrence.startDate || schedule.date || template.date || template.startDate || "one-time")
      : `weekday-${schedule.weekday ?? template.weekday ?? template.dayOfWeek ?? recurrence.daysOfWeek?.[0] ?? 0}`;
  const time = recurrence.startTime || schedule.startTime || template.startTime || `${pad(template.startHour ?? 9)}:${pad(template.startMinute ?? 0)}`;
  return `${title}|${frequency}|${dayPart}|${time}`;
}

function serviceTemplateSignature(template = {}) {
  const schedule = template.schedule || {};
  const recurrence = template.recurrence || {};
  const title = normalizeTemplateKey(template.title || template.name || template.serviceType || template.type || "service");
  const frequency = normalizeFrequency(
    typeof template.recurrence === "string" ? template.recurrence : recurrence.frequency || schedule.frequency || template.frequency,
    "weekly"
  );
  const inferredDay = inferDayFromTitle(template.title || template.name);
  const dayPart = frequency === "monthly"
    ? `day-${recurrence.dayOfMonth || schedule.dayOfMonth || template.dayOfMonth || 1}`
    : frequency === "one_time"
      ? normalizeTemplateKey(recurrence.startDate || schedule.date || template.date || template.startDate || "one-time")
      : `weekday-${schedule.weekday ?? template.weekday ?? template.dayOfWeek ?? recurrence.daysOfWeek?.[0] ?? inferredDay ?? 0}`;
  const time = recurrence.startTime || schedule.startTime || template.startTime || `${pad(template.startHour ?? 9)}:${pad(template.startMinute ?? 0)}`;
  return `${title}|${frequency}|${dayPart}|${time}`;
}

function dedupeServiceTemplates(templates = []) {
  const seen = new Set();
  const signatures = new Set();
  return (Array.isArray(templates) ? templates : [])
    .filter((template) => template?.archived !== true && template?.status !== "archived")
    .filter((template) => normalizeTemplateKey(template?.title || template?.name) !== "service")
    .sort((a, b) => {
      const aGenerated = isGeneratedServiceInstance(a);
      const bGenerated = isGeneratedServiceInstance(b);
      if (aGenerated !== bGenerated) return aGenerated ? 1 : -1;
      return 0;
    })
    .filter((template) => {
      const key = serviceTemplateIdentity(template);
      const signature = serviceTemplateSignature(template);
      if (seen.has(key) || signatures.has(signature)) return false;
      seen.add(key);
      signatures.add(signature);
      return true;
    });
}

function uniqueDays(days) {
  const seen = new Set();
  return (Array.isArray(days) ? days : [])
    .map(normalizeDay)
    .filter(Boolean)
    .filter((day) => {
      if (seen.has(day)) return false;
      seen.add(day);
      return true;
    });
}

export function normalizeServiceTemplate(template = {}, orgTimeZone = DEFAULT_TIME_ZONE) {
  const schedule = template.schedule || {};
  const online = template.online || {};
  const start = parseTime(
    template?.recurrence?.startTime || schedule.startTime || template.startTime,
    clampInt(template.startHour ?? template.hour, 0, 23, 9),
    clampInt(template.startMinute ?? template.minute, 0, 59, 0)
  );
  const legacyDay = normalizeDay(template?.recurrence?.weekday ?? schedule.weekday ?? template.weekday ?? template.dayOfWeek) || inferDayFromTitle(template.title || template.name);
  const days = uniqueDays(
    template?.recurrence?.daysOfWeek || template.daysOfWeek || template.weekdays || (legacyDay ? [legacyDay] : [])
  );
  const startDate = parseDateInput(template?.recurrence?.startDate || schedule.startDate || template.startDate) || new Date();
  const recurrenceValue =
    typeof template.recurrence === "string"
      ? template.recurrence
      : template?.recurrence?.frequency;
  const frequency = normalizeFrequency(recurrenceValue || schedule.frequency || template.frequency, days.length ? "weekly" : "weekly");
  const durationMinutes = clampInt(
    template?.recurrence?.durationMinutes ?? schedule.durationMinutes ?? template.durationMinutes,
    1,
    1440,
    DEFAULT_DURATION
  );
  const checkIn = template.checkIn || {};
  const visibility = template.visibility || {};
  const meetingMode = normalizeMeetingMode(
    template.meetingMode ||
      (template.occurrenceType === "online" || template.occurrenceType === "hybrid"
        ? template.occurrenceType
        : template.isOnline
          ? "online"
          : "in_person")
  );
  const onlineUrl = sanitizeOnlineUrl(template.onlineUrl || online.joinUrl || template.livestreamUrl || template.liveUrl);

  return {
    id: safeText(template.id || template.key || template.templateId, "service"),
    key: safeText(template.key || template.id || template.templateId, "service"),
    enabled: template.enabled !== false && template.isActive !== false,
    title: safeText(template.title || template.name, "Service"),
    description: safeText(template.description || template.summary, ""),
    serviceType: safeText(template.serviceType || template.type, "general_service"),
    exceptions: template && template.exceptions && typeof template.exceptions === "object" ? template.exceptions : null,
    locationName: safeText(template.location?.name || template.locationName || template.location || template.venue, ""),
    address: safeText(template.location?.address || template.address || template.fullAddress || template.serviceAddress, ""),
    room: safeText(template.room, ""),
    isOnline: meetingMode === "online" || meetingMode === "hybrid",
    livestreamUrl: onlineUrl,
    occurrenceType: meetingMode === "in_person" ? "in-person" : meetingMode,
    meetingMode,
    onlineProvider: normalizeOnlineProvider(template.onlineProvider || online.provider),
    onlineUrl,
    onlineButtonLabel: safeText(template.onlineButtonLabel || online.buttonLabel, ""),
    onlineStartVisibility: normalizeVisibility(template.onlineStartVisibility || online.startVisibility),
    onlineInstructions: safeText(template.onlineInstructions || online.instructions, ""),
    online: {
      provider: normalizeOnlineProvider(template.onlineProvider || online.provider),
      joinUrl: onlineUrl,
      buttonLabel: safeText(template.onlineButtonLabel || online.buttonLabel, ""),
      startVisibility: normalizeVisibility(template.onlineStartVisibility || online.startVisibility),
      instructions: safeText(template.onlineInstructions || online.instructions, ""),
    },
    recurrence: {
      frequency,
      interval: clampInt(template?.recurrence?.interval ?? template.interval, 1, 24, 1),
      daysOfWeek: days.length ? days : ["sun"],
      dayOfMonth: template?.recurrence?.dayOfMonth ?? schedule.dayOfMonth ?? template.dayOfMonth ?? startDate.getDate(),
      monthOfYear: template?.recurrence?.monthOfYear ?? schedule.monthOfYear ?? template.monthOfYear ?? startDate.getMonth() + 1,
      ordinal: Number.isFinite(Number(template?.recurrence?.ordinal)) ? Number(template.recurrence.ordinal) : undefined,
      exdate: Array.isArray(template?.recurrence?.exdate) ? template.recurrence.exdate.map((d) => String(d)) : [],
      rdate: Array.isArray(template?.recurrence?.rdate) ? template.recurrence.rdate.map((d) => String(d)) : [],
      startDate: toDateInput(startDate),
      endDate: template?.recurrence?.endDate || schedule.endDate || template.endDate || null,
      startTime: `${pad(start.hour)}:${pad(start.minute)}`,
      durationMinutes,
      timeZone: safeText(template?.recurrence?.timeZone || schedule.timezone || schedule.timeZone || template.timeZone, orgTimeZone || DEFAULT_TIME_ZONE),
    },
    checkIn: {
      enabled: checkIn.enabled !== false,
      openMinutesBeforeStart: clampInt(
        checkIn.openMinutesBeforeStart ?? template.openMinutesBeforeStart,
        0,
        1440,
        DEFAULT_OPEN_BEFORE
      ),
      closeMinutesAfterEnd: clampInt(
        checkIn.closeMinutesAfterEnd ?? template.closeMinutesAfterEnd ?? checkIn.closeMinutesAfterStart ?? template.closeMinutesAfterStart,
        0,
        1440,
        DEFAULT_CLOSE_AFTER_END
      ),
      closeMinutesAfterStart: clampInt(
        checkIn.closeMinutesAfterStart ?? template.closeMinutesAfterStart ?? checkIn.closeMinutesAfterEnd ?? template.closeMinutesAfterEnd,
        0,
        1440,
        DEFAULT_CLOSE_AFTER
      ),
      allowLateCheckIn: checkIn.allowLateCheckIn !== false,
    },
    visibility: {
      showOnPublicWebsite: visibility.showOnPublicWebsite !== false && visibility.isPublic !== false,
      showInCheckIn: visibility.showInCheckIn !== false,
      showInMemberProfile: visibility.showInMemberProfile !== false,
    },
    order: clampInt(template.order, 0, 999, 0),
    sourceTemplate: template,
  };
}

function templateOccursOnDate(template, dateParts) {
  const startDate = parseLocalDateParts(template.recurrence.startDate);
  const endDate = parseLocalDateParts(template.recurrence.endDate);
  // startDate lower-bound and endDate upper-bound remain client-side: the engine
  // matcher has no endDate concept (endDate is not settable via any supported UI,
  // so this is a dormant, behavior-preserving guard).
  if (startDate && compareLocalParts(dateParts, startDate) < 0) return false;
  if (endDate && compareLocalParts(dateParts, endDate) > 0) return false;

  // Delegate the frequency/interval/day match to the Canonical Service Engine.
  // enabled/showInCheckIn are already filtered upstream in getNextServiceOccurrences,
  // so they are passed true here to make the engine's gate a no-op.
  return engineServiceTemplateMatchesParts(
    {
      enabled: true,
      showInCheckIn: true,
      startParts: startDate,
      frequency: template.recurrence.frequency,
      daysOfWeek: template.recurrence.daysOfWeek || [],
      interval: template.recurrence.interval || 1,
      dayOfMonth: Number(template.recurrence.dayOfMonth || (startDate && startDate.day) || 1),
      monthOfYear: Number(template.recurrence.monthOfYear || (startDate && startDate.month) || 1),
      ordinal: template.recurrence.ordinal,
      exdate: template.recurrence.exdate,
      rdate: template.recurrence.rdate,
    },
    dateParts,
    dateParts.weekday
  );
}

function exceptionForDate(template, dateParts) {
  const map = template && template.exceptions;
  if (!map || typeof map !== "object") return null;
  const key = `${dateParts.year}-${pad(dateParts.month)}-${pad(dateParts.day)}`;
  return map[key] || null;
}

function occurrenceFromTemplate(template, dateParts, exception) {
  // Overlay a per-instance override (RECURRENCE-ID) onto the template's schedule.
  // Immutable fields (serviceType, timeZone) come from the template; the engine
  // overlay ignores immutable keys. Check-in windows are recomputed below from the
  // (possibly overridden) start/end + offsets.
  const timeZone = template.recurrence.timeZone || DEFAULT_TIME_ZONE; // immutable
  const draft = engineApplyOccurrenceOverride(
    {
      title: template.title,
      startTimeLocal: template.recurrence.startTime,
      durationMinutes: template.recurrence.durationMinutes,
      checkIn: {
        enabled: template.checkIn.enabled,
        openMinutesBeforeStart: template.checkIn.openMinutesBeforeStart,
        closeMinutesAfterEnd: template.checkIn.closeMinutesAfterEnd,
      },
      visibility: { ...template.visibility },
      location: { name: template.locationName, address: template.address },
    },
    exception
  );

  const { hour, minute } = parseTime(draft.startTimeLocal, 9, 0);
  let durationMinutes = clampInt(draft.durationMinutes, 1, 1440, DEFAULT_DURATION);
  if (draft.endTimeLocal) {
    const end = parseTime(draft.endTimeLocal, hour, minute);
    const delta = (end.hour * 60 + end.minute) - (hour * 60 + minute);
    if (delta > 0) durationMinutes = delta;
  }
  const openMinutesBeforeStart = clampInt(
    draft.checkIn && draft.checkIn.openMinutesBeforeStart,
    0,
    1440,
    template.checkIn.openMinutesBeforeStart
  );
  const closeMinutesAfterEnd = clampInt(
    draft.checkIn && draft.checkIn.closeMinutesAfterEnd,
    0,
    1440,
    template.checkIn.closeMinutesAfterEnd
  );

  const startAt = buildDateFromPartsInTimeZone(dateParts.year, dateParts.month, dateParts.day, hour, minute, timeZone);
  const endAt = new Date(startAt.getTime() + durationMinutes * 60 * 1000);
  const checkInOpenAt = new Date(startAt.getTime() - openMinutesBeforeStart * 60 * 1000);
  const checkInCloseAt = new Date(endAt.getTime() + closeMinutesAfterEnd * 60 * 1000);
  const dateKey = `${dateParts.year}-${pad(dateParts.month)}-${pad(dateParts.day)}`;
  const timeKey = `${pad(hour)}${pad(minute)}`;
  const effectiveCheckIn = {
    ...template.checkIn,
    ...(draft.checkIn || {}),
    openMinutesBeforeStart,
    closeMinutesAfterEnd,
  };
  const effectiveVisibility = draft.visibility || template.visibility;
  const effectiveLocationName = (draft.location && draft.location.name) || template.locationName;
  const effectiveAddress = (draft.location && draft.location.address) || template.address;

  return {
    templateId: template.id,
    occurrenceId: `${template.id}_${dateKey}_${timeKey}`,
    title: draft.title,
    description: template.description,
    serviceType: template.serviceType,
    // The organization's timezone travels with the occurrence so every
    // display surface can format wall-clock times as the CHURCH's local
    // time — never the viewer's device timezone.
    timeZone: template.recurrence.timeZone || DEFAULT_TIME_ZONE,
    startAt,
    endAt,
    checkInOpenAt,
    checkInCloseAt,
    locationName: effectiveLocationName,
    address: effectiveAddress,
    room: template.room,
    isOnline: template.isOnline,
    livestreamUrl: template.livestreamUrl,
    occurrenceType: template.occurrenceType,
    meetingMode: template.meetingMode,
    onlineProvider: template.onlineProvider,
    onlineUrl: template.onlineUrl,
    onlineButtonLabel: template.onlineButtonLabel,
    onlineStartVisibility: template.onlineStartVisibility,
    onlineInstructions: template.onlineInstructions,
    online: template.online,
    recurrence: template.recurrence,
    checkIn: effectiveCheckIn,
    visibility: effectiveVisibility,
    sourceTemplate: template.sourceTemplate,
  };
}

export function getNextServiceOccurrences(templates = [], options = {}) {
  const from = options.from instanceof Date ? options.from : new Date();
  const days = clampInt(options.days, 1, 365, 30);
  const surface = options.surface || "public";
  const orgTimeZone = options.orgTimeZone || DEFAULT_TIME_ZONE;
  const normalized = dedupeServiceTemplates(templates)
    .map((template, index) => ({ ...normalizeServiceTemplate(template, orgTimeZone), order: Number(template?.order ?? index) }))
    .filter((template) => template.enabled)
    .filter((template) => {
      if (options.includeHidden) return true;
      if (surface === "public") return template.visibility.showOnPublicWebsite !== false;
      if (surface === "checkIn") return template.visibility.showInCheckIn !== false;
      if (surface === "member") return template.visibility.showInMemberProfile !== false;
      return true;
    });

  const fromParts = getDatePartsInTimeZone(from, orgTimeZone);
  const startOrdinal = Date.UTC(fromParts.year, fromParts.month - 1, fromParts.day);
  const occurrences = [];
  for (let offset = 0; offset < days; offset += 1) {
    const date = localPartsFromOrdinal(startOrdinal + offset * 24 * 60 * 60 * 1000, orgTimeZone);
    normalized.forEach((template) => {
      if (!templateOccursOnDate(template, date)) return;
      const exception = exceptionForDate(template, date);
      if (engineIsOccurrenceCancelled(exception)) return; // cancelled instance is suppressed
      occurrences.push(occurrenceFromTemplate(template, date, exception));
    });
  }

  return occurrences
    .filter((occurrence) => occurrence.startAt.getTime() >= from.getTime())
    .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
}

export function isServiceCheckInOpen(occurrence, now = new Date()) {
  if (!occurrence?.checkIn?.enabled) return false;
  const time = now instanceof Date ? now.getTime() : new Date(now).getTime();
  return time >= occurrence.checkInOpenAt.getTime() && time <= occurrence.checkInCloseAt.getTime();
}

export function getActiveCheckInWindow(occurrences = [], now = new Date()) {
  return occurrences
    .filter((occurrence) => isServiceCheckInOpen(occurrence, now))
    .sort((a, b) => Math.abs(a.startAt - now) - Math.abs(b.startAt - now))[0] || null;
}

// Service times always display in the organization's timezone — the time the
// service starts at the church — regardless of the viewer's device timezone.
export function occurrenceTimeZone(occurrence) {
  return (
    occurrence?.timeZone ||
    occurrence?.recurrence?.timeZone ||
    DEFAULT_TIME_ZONE
  );
}

function orgLocalTime(occurrence, { withZoneName = false } = {}) {
  if (!(occurrence?.startAt instanceof Date) || Number.isNaN(occurrence.startAt.getTime())) return "";
  return occurrence.startAt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: occurrenceTimeZone(occurrence),
    ...(withZoneName ? { timeZoneName: "short" } : {}),
  });
}

function orgLocalDate(occurrence, options) {
  return occurrence.startAt.toLocaleDateString(undefined, {
    ...options,
    timeZone: occurrenceTimeZone(occurrence),
  });
}

export function getFrequencyLabel(occurrence) {
  const recurrence = occurrence?.recurrence || {};
  const time = orgLocalTime(occurrence, { withZoneName: true });
  if (recurrence.frequency === "weekly") {
    const days = (recurrence.daysOfWeek || []).map((day) => DAY_LABELS[day]).filter(Boolean).join(", ");
    return `${days ? `Every ${days}` : "Weekly"}${time ? ` · ${time}` : ""}`;
  }
  if (recurrence.frequency === "monthly") return `Monthly on day ${recurrence.dayOfMonth}${time ? ` · ${time}` : ""}`;
  if (recurrence.frequency === "quarterly") return `Quarterly${time ? ` · ${time}` : ""}`;
  if (recurrence.frequency === "annually") return `Annually${time ? ` · ${time}` : ""}`;
  return `One-time${time ? ` · ${time}` : ""}`;
}

export function formatServiceTime(occurrence) {
  if (!occurrence?.startAt || Number.isNaN(occurrence.startAt.getTime())) {
    return {
      title: safeText(occurrence?.title, "Service"),
      frequencyLabel: "Service time coming soon",
      timeLabel: "Time coming soon",
      dateLabel: "Upcoming",
      locationLabel: safeText(occurrence?.locationName || occurrence?.address, "Address available on request"),
      mapsUrl: getMapsUrl(occurrence?.address || occurrence?.locationName),
    };
  }
  return {
    title: safeText(occurrence.title, "Service"),
    frequencyLabel: getFrequencyLabel(occurrence),
    timeLabel: orgLocalTime(occurrence, { withZoneName: true }),
    dateLabel: orgLocalDate(occurrence, { weekday: "short", month: "short", day: "numeric" }),
    locationLabel: safeText(occurrence.locationName || occurrence.address, "Address available on request"),
    mapsUrl: getMapsUrl(occurrence.address || occurrence.locationName),
  };
}

export function generateNextOccurrence(service, options = {}) {
  const [occurrence] = getNextServiceOccurrences([service], {
    days: options.days || 365,
    from: options.from || new Date(),
    orgTimeZone: options.orgTimeZone || DEFAULT_TIME_ZONE,
    surface: "public",
  });
  if (!occurrence) return null;
  return {
    date: occurrence.startAt,
    formattedDate: orgLocalDate(occurrence, {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).toUpperCase(),
    formattedTime: orgLocalTime(occurrence, { withZoneName: true }),
    label: getFrequencyLabel(occurrence),
    occurrence,
  };
}

// ---------------------------------------------------------------------------
// Canonical read path (5E). EXACTLY ONE read source: the canonical
// serviceTemplates subcollection (hydrated onto org.serviceTemplateDocs). No
// org-doc array fallback, no publicProfile fallback, no shadow dual-read, no
// feature flag.
// ---------------------------------------------------------------------------

// The single canonical source. Returns the subcollection template docs only.
export function resolveCanonicalServiceTemplates(org) {
  return Array.isArray(org?.serviceTemplateDocs) ? org.serviceTemplateDocs : [];
}

// The canonical subcollection (5A editor) stores canonical-shaped docs while the
// occurrence engine reads the legacy shape. Coerce canonical-shaped docs via the
// canonical->legacy adapter; legacy-shaped docs pass through unchanged.
function looksCanonicalTemplate(t) {
  return Boolean(t && t.recurrence && typeof t.recurrence.type === "string" && t.startTime);
}
function coerceTemplateForRead(t) {
  return looksCanonicalTemplate(t) ? canonicalToLegacyTemplate(t) : t;
}

export function getPublicServiceOccurrences(org, options = {}) {
  return getNextServiceOccurrences(resolveCanonicalServiceTemplates(org).map(coerceTemplateForRead), {
    days: options.days || 30,
    from: options.from || new Date(),
    orgTimeZone: org?.timeZone || org?.publicProfile?.timeZone || org?.profile?.timeZone || DEFAULT_TIME_ZONE,
    surface: "public",
  });
}
